/**
 * ContractPreview - Preview în timp real al contractului în format A4
 * Folosește funcția comună generateContractHTML pentru consistență
 */
import { useMemo, useRef, useEffect, useState } from 'react';
import { FileText, Printer, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { generateContractHTML } from './contractUtils';

export default function ContractPreview({ contractData, companyData, onPrint, onDownload }) {
  const contractHTML = useMemo(() => {
    return generateContractHTML(contractData, companyData, { forPrint: false });
  }, [contractData, companyData]);

  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(3000);
  const [scale, setScale] = useState(0.75);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const adjustHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc && doc.body) {
          const h = doc.body.scrollHeight || doc.documentElement.scrollHeight;
          if (h > 100) setIframeHeight(h + 40);
        }
      } catch (error) {
        console.error('Failed to adjust iframe height:', error);
      }
    };
    iframe.addEventListener('load', adjustHeight);
    const timer = setTimeout(adjustHeight, 600);
    return () => { iframe.removeEventListener('load', adjustHeight); clearTimeout(timer); };
  }, [contractHTML]);

  // Responsive scale — fit A4 (≈794px wide) to container
  useEffect(() => {
    const computeScale = () => {
      const el = wrapRef.current;
      if (!el) return;
      const w = el.clientWidth - 16; // padding buffer
      const A4_WIDTH = 820;
      const s = Math.min(1, Math.max(0.35, w / A4_WIDTH));
      setScale(s);
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-3 bg-slate-100 border-b">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <FileText className="w-4 h-4" />
          <span>Preview Contract (Format A4)</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onPrint}>
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="w-4 h-4 mr-1" />
            Descarca PDF
          </Button>
        </div>
      </div>

      <div ref={wrapRef} className="flex-1 overflow-auto bg-slate-200 p-2 sm:p-4">
        <div
          className="mx-auto"
          style={{
            width: `${Math.floor(820 * scale)}px`,
            height: `${Math.floor(iframeHeight * scale)}px`,
            maxWidth: '100%',
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={contractHTML}
            title="Contract Preview"
            className="border-0 bg-white"
            style={{ 
              width: '820px',
              height: `${iframeHeight}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              display: 'block',
            }}
          />
        </div>
      </div>

      <div className="p-2 bg-blue-50 border-t border-blue-200 text-xs text-blue-700 text-center">
        Datele completate sunt afisate cu <strong>text ingrosat (bold)</strong>
      </div>
    </div>
  );
}
